import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useSearchStudents } from '../hooks/useStudents';
import { StudentSearchResultRow } from './StudentSearchResultRow';
import { Spinner } from '@/shared/components/Spinner';
export function StudentSearchBar({ onSelect, placeholder = 'Buscar por nº, nombre, DNI o email…', autoFocus = false, filterOppositionId, }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const debounced = useDebounce(query, 250);
    const { data: results, isFetching } = useSearchStudents(debounced);
    // Opposition scoping (client-side over the ≤10 hits; see prop docs).
    const visible = filterOppositionId
        ? (results ?? []).filter((r) => r.opposition_id === filterOppositionId)
        : results ?? [];
    // Reset active index when the offered results change
    useEffect(() => {
        setActiveIndex(0);
    }, [results, filterOppositionId]);
    // Close on outside click
    useEffect(() => {
        function handleClick(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        if (open)
            document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);
    function handleKeyDown(e) {
        if (visible.length === 0) {
            if (e.key === 'Escape') {
                setOpen(false);
                inputRef.current?.blur();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const r = visible[activeIndex];
            if (r)
                handleSelect(r);
        }
        else if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
        }
    }
    function handleSelect(r) {
        setOpen(false);
        setQuery('');
        onSelect(r);
        void navigate; // placate unused import while keeping ergonomic for callers
    }
    const showDropdown = open && debounced.trim().length >= 1;
    return (_jsxs("div", { ref: containerRef, className: "relative w-full", children: [_jsxs("div", { className: "relative", children: [_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, className: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400", "aria-hidden": "true", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })] }), _jsx("input", { ref: inputRef, type: "search", value: query, onChange: (e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }, onFocus: () => setOpen(true), onKeyDown: handleKeyDown, placeholder: placeholder, autoFocus: autoFocus, autoComplete: "off", spellCheck: false, className: "w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" }), isFetching && (_jsx("span", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: _jsx(Spinner, { size: "sm" }) }))] }), showDropdown && (_jsxs("div", { role: "listbox", className: "absolute z-40 mt-1 max-h-96 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900", children: [!results && isFetching && (_jsx("div", { className: "flex items-center justify-center py-8", children: _jsx(Spinner, { size: "md" }) })), results && visible.length === 0 && !isFetching && (_jsxs("div", { className: "px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400", children: ["Sin resultados para \"", debounced, "\""] })), results && visible.length > 0 && (_jsx("ul", { className: "divide-y divide-gray-100 dark:divide-gray-800", children: visible.map((r, idx) => (_jsx("li", { children: _jsx(StudentSearchResultRow, { result: r, active: idx === activeIndex, onSelect: () => handleSelect(r) }) }, r.id))) }))] }))] }));
}
//# sourceMappingURL=StudentSearchBar.js.map